import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Search, Package, Flame } from "lucide-react";
import { catColor, catLabel, euroPrecise } from "../lib/constants";

export function CriticalBreakdown({ breakdown, cost_per_portion }) {
    const navigate = useNavigate();
    if (!breakdown || breakdown.length === 0) {
        return (
            <div className="text-sm text-brand-muted italic">
                Nessun ingrediente trovato per questo piatto.
            </div>
        );
    }

    // Gli "hero" sono i primi 1-3 che sommano ~70% del costo
    const heroes = breakdown.filter((b) => b.is_pareto_critical);
    const heroesText =
        heroes.length === 1
            ? `${heroes[0].contribute_pct.toFixed(0)}% del costo viene da un solo ingrediente`
            : `${heroes.length} ingredienti fanno ${heroes[heroes.length - 1]?.cumulative_pct.toFixed(0)}% del costo`;

    return (
        <div className="mt-4 space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-white border border-brand-gold/35 px-4 py-3">
                <div className="h-9 w-9 rounded-full bg-brand-gold/15 text-brand-goldDark flex items-center justify-center shrink-0">
                    <Flame size={16} />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-brand-goldDark font-bold">
                        Analisi Pareto
                    </div>
                    <div className="font-display text-lg text-brand-ink leading-tight mt-0.5">
                        {heroesText}
                    </div>
                    <div className="text-[12px] text-brand-muted mt-1">
                        Concentrati su questi per ottenere il massimo risparmio con il minimo sforzo.
                    </div>
                </div>
            </div>

            <div className="space-y-1.5">
                {breakdown.map((b, i) => (
                    <div
                        key={b.ingredient_id}
                        className="flex items-center gap-3 rounded-lg bg-white border border-brand-line px-3 py-2.5"
                        style={b.is_pareto_critical ? { borderColor: `${catColor(b.category)}66`, background: `${catColor(b.category)}08` } : undefined}
                        data-testid={`breakdown-row-${i}`}
                    >
                        <span
                            className="w-1.5 h-10 rounded-full shrink-0"
                            style={{ background: catColor(b.category) }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-semibold text-brand-ink truncate">
                                    {b.name}
                                </div>
                                {b.is_pareto_critical && (
                                    <span
                                        className="text-[9px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5"
                                        style={{ background: "#d32f2f", color: "#fff" }}
                                    >
                                        Critico
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-brand-muted mt-0.5">
                                {b.quantity} {b.quantity_unit} · {euroPrecise(b.cost_per_unit)}/{b.unit}
                                {b.waste_pct > 0 && ` · scarto ${b.waste_pct}%`}
                            </div>
                        </div>
                        <div className="w-28 hidden sm:block">
                            <div className="h-1.5 rounded-full bg-brand-cream overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{
                                        width: `${Math.min(100, b.contribute_pct)}%`,
                                        background: catColor(b.category),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-display text-base text-brand-ink leading-none">
                                {b.contribute_pct.toFixed(1)}%
                            </div>
                            <div className="text-[10px] text-brand-muted mt-0.5">
                                € {b.contribute_cost.toFixed(2)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {heroes.length > 0 && (
                <div className="mt-4 rounded-lg bg-brand-navy text-white p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-gold mb-2">
                        Azioni consigliate
                    </div>
                    <ul className="space-y-2 text-[13px]">
                        {heroes.map((h) => (
                            <li key={h.ingredient_id} className="flex items-start gap-2">
                                <Search size={14} className="text-brand-gold mt-0.5 shrink-0" />
                                <span>
                                    <strong>«{h.name}»</strong> pesa {h.contribute_pct.toFixed(1)}% del costo. Cerca un fornitore
                                    alternativo sotto <strong>{euroPrecise(h.cost_per_unit * 0.85)}/{h.unit}</strong> per
                                    tagliare ~15% del costo totale del piatto.
                                </span>
                            </li>
                        ))}
                        <li className="flex items-start gap-2">
                            <Package size={14} className="text-brand-gold mt-0.5 shrink-0" />
                            <button
                                onClick={() => heroes[0] && navigate(`/ingredients/${heroes[0].ingredient_id}`)}
                                className="text-left underline decoration-brand-gold/40 hover:decoration-brand-gold transition"
                            >
                                Apri la scheda di «{heroes[0]?.name}» per aggiornare prezzo e fornitore
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
}

export function CriticalBreakdownToggle({ details }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                onClick={() => setOpen((s) => !s)}
                data-testid={`analyze-btn-${details.id}`}
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand-navy bg-brand-navy text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-brand-navyLight transition"
            >
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {open ? "Nascondi analisi" : "Analizza ingredienti critici"}
            </button>
            {open && (
                <div data-testid={`breakdown-${details.id}`}>
                    <CriticalBreakdown breakdown={details.breakdown} cost_per_portion={details.cost_per_portion} />
                </div>
            )}
        </div>
    );
}
